import { useTranslation } from 'react-i18next';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { SocialAccountType } from '@anytag/graphql';
import { defaultAvatarImage } from '@anytag/shared-assets';
import { Anchor, SocialIcon, MediaPreview, SocialIconProps, MediaPreviewProps } from '@anytag/shared-atoms';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';

export interface InfluencerAvatarProps extends Pick<MediaPreviewProps, 'src' | 'objectFit'> {
  id?: number | string | null;
  size?: string;
  asChild?: boolean;
  className?: string;
  disabled?: boolean;
  src?: string | null;
  isBrandAccount?: boolean;
  socialAccountType?: SocialAccountType | '';
  search?: {
    filter?: {
      id: string;
      sm?: SocialAccountType;
    };
  };
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const InfluencerAvatar = ({
  id,
  src,
  asChild,
  disabled,
  search,
  className,
  objectFit = 'contain',
  size = '32px',
  isBrandAccount,
  socialAccountType,
  onClick,
  ...restProps
}: InfluencerAvatarProps) => {
  const { t } = useTranslation();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const profileLink = generateProfileLink(id);

  const Comp = asChild ? Slot : Anchor;

  return (
    <Comp
      {...profileLink}
      search={search}
      disabled={disabled || (!id && !onClick)}
      className={cn(styles.anchor, className)}
      onClick={onClick}
      style={{ width: size, height: size }}
      {...restProps}
    >
      {/* if asChild=true then Comp props will pass into Slottable */}
      <Slottable>
        <MediaPreview
          src={src}
          alt="avatar"
          objectFit={objectFit}
          defaultImage={defaultAvatarImage}
          className="bg-inherit"
          style={{ width: size, height: size }}
        />
      </Slottable>
      {socialAccountType ? (
        <div className={styles.socialIconWrapper}>
          <SocialIcon size="16px" type={socialAccountType as SocialIconProps['type']} css={{ display: 'block' }} />
        </div>
      ) : null}
      {isBrandAccount && <div className={styles.brandBadge}>{t('Option.Brand')}</div>}
    </Comp>
  );
};

const styles = {
  anchor: cn('relative box-border flex-none rounded-md bg-white'),
  socialIconWrapper: cn('absolute top-0 left-0 rounded-r-sm bg-white p-1'),
  brandBadge: cn(
    'absolute bottom-0 left-0 box-border w-full rounded-b-md bg-black-90 p-0.5 text-center text-xxs leading-3.5 text-white'
  )
};
